import type { IntlShape } from 'react-intl'

import { cloneDeep } from 'lodash-es'

import { FieldSchema, FieldValue } from '../../models/formMetadata/FormMetadata'
import { Note, NoteMetadata } from '../../models/notes/Notes'
import { getVisibleFields, validateForm } from '../assignments/utils'

const ADDENDUM_FIELD_NAME = 'addendum'

export const DA_FLEXIBLE_NOTE_REVERSED_CHARGE_EDITABLE_FIELDS = [
  'attendance',
  'changeReason',
  'changeNotice',
  'otherChangeReason',
  'technicalIssuesCancelSelection',
  'technicalIssuesCancelDetails',
  'modality',
  'session_duration',
  'icd10_primary_diagnosis',
  'icd10_secondary_diagnosis',
] as const

const WIDGETS_REQUIRING_SCHEMA_READ_ONLY = [
  'text',
  'checkboxGroup',
  'textarea',
  'typeAhead',
  'typeAheadWithShowAll',
  'selectWithCta',
]

export const setFieldsEditable = (metaData: NoteMetadata['meta_data'], fieldNames: string[]): void => {
  if (!metaData?.uiSchema) {
    return
  }

  const { uiSchema, schema } = metaData

  fieldNames.forEach((fieldName: string) => {
    if (uiSchema[fieldName]) {
      delete uiSchema[fieldName]['ui:readonly']
      delete uiSchema[fieldName]['ui:disabled']
    }
    const widgetProps = schema?.properties?.[fieldName]?.widgetProps
    if (widgetProps) {
      delete widgetProps.readOnly
    }
  })
}

export const setFieldsReadOnly = (metaData: NoteMetadata['meta_data'], fieldNames: string[]): void => {
  if (!metaData?.uiSchema) {
    return
  }

  const { uiSchema } = metaData

  fieldNames.forEach((fieldName: string) => {
    const widgetType = uiSchema[fieldName]?.['ui:widget']

    if (uiSchema[fieldName]) {
      if (WIDGETS_REQUIRING_SCHEMA_READ_ONLY.includes(widgetType) && metaData?.schema?.properties?.[fieldName]) {
        metaData.schema.properties[fieldName].widgetProps = {
          ...metaData?.schema?.properties?.[fieldName].widgetProps,
          readOnly: true,
        }
      } else {
        uiSchema[fieldName] = {
          ...uiSchema[fieldName],
          ...(!WIDGETS_REQUIRING_SCHEMA_READ_ONLY.includes(widgetType) && { 'ui:readonly': true }),
          'ui:disabled': true,
        }
      }
    } else {
      uiSchema[fieldName] = {
        'ui:readonly': true,
        'ui:disabled': true,
      }
    }
  })
}

/**
 * Transforms note metadata for reversed charge notes:
 * - Locks (read-only + disabled) every currently-visible field, preserving the originally
 *   submitted content. Conditionally-hidden fields are left untouched so they stay editable
 *   if the provider later reveals them (e.g. by selecting a note format).
 * - Re-opens only REQUIRED visible fields that currently fail validation (empty or invalid),
 *   so the provider can complete/correct them — otherwise the note can never be submitted.
 *   Optional fields stay locked regardless, since we can't infer the original intent for them.
 * - ALWAYS re-opens explicitly provided editable fields (e.g. billing questions for flexible note)
 * - Adds an addendum textarea field at the beginning (if not already present)
 *
 * Autosave persists raw input without running form validation, so note.body can hold empty
 * or invalid values. We re-run the form's own `validateForm` to decide which required fields
 * the form would currently reject — those are the ones that would block submission.
 *
 * @param contentMetadata - The original note content_meta_data
 * @param config - Validation values, intl, and explicitly editable fields
 * @returns A new content_meta_data object with read-only fields and addendum field added
 */
export const getReversedChargeNoteMetadata = (
  contentMetadata: NoteMetadata,
  {
    noteValues,
    formatMessage,
    externalValues,
    editableFieldNames = [],
  }: {
    noteValues: Note['body']
    formatMessage: IntlShape['formatMessage']
    externalValues?: { [key: string]: FieldValue }
    editableFieldNames?: readonly string[]
  },
): NoteMetadata => {
  const cloned = cloneDeep(contentMetadata)

  if (!cloned.meta_data) {
    return cloned
  }

  const { meta_data } = cloned
  const uiSchema = meta_data.uiSchema
  const schema = meta_data.schema

  if (uiSchema && uiSchema['ui:order']) {
    const values = { ...noteValues, ...externalValues } as { [key: string]: FieldValue }

    const validationErrors = validateForm({
      fields: schema?.properties,
      values,
      formatMessage,
      schema: schema as FieldSchema,
    })

    // Only touch fields the provider can currently see. Conditionally-hidden fields (e.g.
    // note-format-specific fields when that format isn't selected) are left untouched, so
    // they stay editable if the provider later reveals them by changing the controlling
    // field — otherwise they'd surface locked-and-empty and re-block submission.
    const visibleFields = (getVisibleFields({ uiSchema, schema: schema as FieldSchema, values }) ?? []).filter(
      (fieldName: string) => fieldName !== ADDENDUM_FIELD_NAME,
    )

    // Re-open a visible field only if it is required AND currently fails validation (empty
    // or invalid) — those are the fields that would block submission. Optional fields, and
    // required fields with valid values, stay locked to preserve submitted content.
    const editableFieldsSet = new Set<string>(editableFieldNames)
    const editableFields = visibleFields.filter((fieldName: string) => {
      const validation = schema?.properties?.[fieldName]?.validation
      const isRequired = Boolean(validation?.isRequired || validation?.requiredIf)
      const isInvalid = Boolean(validationErrors[fieldName])
      return editableFieldsSet.has(fieldName) || (isRequired && isInvalid)
    })

    // Lock every visible field, then re-open just the required ones the provider must complete.
    setFieldsReadOnly(meta_data, visibleFields)
    setFieldsEditable(meta_data, editableFields)
  }

  // Only add addendum field if it doesn't already exist
  if (schema?.properties && !schema.properties.addendum) {
    schema.properties.addendum = {
      name: 'addendum',
      title: 'Addendum',
      placeholder: 'Please include a reason for resubmission',
      type: 'string',
      validation: {
        isRequired: true,
      },
    }
  }

  if (uiSchema) {
    if (!uiSchema[ADDENDUM_FIELD_NAME]) {
      uiSchema[ADDENDUM_FIELD_NAME] = {
        'ui:widget': 'textarea',
      }
    }

    if (uiSchema['ui:order'] && !uiSchema['ui:order'].includes('addendum')) {
      uiSchema['ui:order'].unshift('addendum')
    }
  }

  return cloned
}
